import type { FC, PropsWithChildren } from "react";
import { useEffect, useRef } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";

import { ConsentCheckbox } from "@ui/components/ContactForm";
import { Controller, Form, useForm } from "@ui/components/Form";
import { formatFileSize, formatFileTypes } from "@ui/components/LoyaltyContactForm/utils";
import { Recaptchabar } from "@ui/components/Recaptchabar";
import { Typography } from "@ui/components/Typography";
import { useCurrentBreakpoint } from "@ui/hooks/useCurrentBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorCode, getBackendErrorFields } from "@ui/utils/backendError";

import { useConfig } from "@jti/app-context/useConfig";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import type { FilePickerProps } from "@jti/core-components/components/FilePicker";
import { FilePicker } from "@jti/core-components/components/FilePicker";
import { toast } from "@jti/core-components/components/Toaster";
import type { Steps } from "@jti/fe-common";
import { getErrorMessageFromError } from "@jti/fe-common";
import { useConsentFields, useLoyaltyContestSubmit } from "@jti/rest-api";

import styles from "./LoyaltyContestFormProvider.module.scss";

export type LoyaltyContestFormProviderProps = {
  title: string;
  description?: string;
  sendButtonLabel?: string;
  isFilePickerVisible?: boolean;
  successScreenTitle?: string;
  filePickerProps?: FilePickerProps;
  consent?: Steps;
  showRequiredAsterisk?: boolean;
  onSuccess?: () => void;
  contestId?: string;
};

export const LoyaltyContestFormProvider: FC<PropsWithChildren<LoyaltyContestFormProviderProps>> = ({
  title,
  children,
  description,
  sendButtonLabel,
  successScreenTitle,
  filePickerProps,
  isFilePickerVisible,
  showRequiredAsterisk = false,
  consent = "contact_form",
  onSuccess,
  contestId,
}) => {
  const form = useForm({ mode: "onTouched" });
  const { __, dictionary } = useTranslations();
  const { user } = useUser();
  const { mainBreakpoint } = useCurrentBreakpoint();
  const { reCaptchaEnabled, brandingInformationEnabled } = useConfig();
  const { consentFields, error: consentError, isLoading: isConsentsLoading } = useConsentFields(consent);
  const { mutateAsync: handleLoyaltySubmit, isSuccess, isLoading } = useLoyaltyContestSubmit();
  const hasNotifiedSuccessRef = useRef(false);

  const { setError } = form;

  useEffect(() => {
    if (!isSuccess || hasNotifiedSuccessRef.current) {
      return;
    }

    hasNotifiedSuccessRef.current = true;
    onSuccess?.();
  }, [isSuccess, onSuccess]);

  const handleContestSubmit: SubmitHandler<FieldValues> = async (formValues: Record<string, unknown>) => {
    if (!user) return;
    try {
      await handleLoyaltySubmit({
        formValues,
        user,
        consentFields,
      });
    } catch (error) {
      const backendErrorFields = getBackendErrorFields(error);
      const fileErrorKeys =
        getBackendErrorCode(error) === "validation_error" && backendErrorFields
          ? Object.keys(backendErrorFields).filter((key) => key.startsWith("files."))
          : [];
      if (fileErrorKeys.length > 0) {
        setError("files", {
          type: "manual",
          message: __("filePicker.error.unsupportedType") || "Unsupported file type.",
        });
      } else {
        const preparedError = getErrorMessageFromError(error ?? {}, __("evo.fallback.error"), dictionary);
        toast.error(preparedError);
      }
    }
  };

  if (consentError) {
    return <AlertBanner tone="error" title={__("loyalty.contest.error.cannot_load")} />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <Typography variant="body1" weight="light" className={styles.headerText}>
          {title}
        </Typography>
        <Typography variant="body1" weight="light" className={styles.description}>
          {description}
        </Typography>
      </div>
      {isSuccess ? (
        <AlertBanner tone="success" title={successScreenTitle} />
      ) : (
        <>
          <Form form={form} onSubmit={handleContestSubmit} className={styles.form} aria-label={title}>
            {children}
            {isFilePickerVisible && filePickerProps && (
              <Controller
                name="files"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <FilePicker
                    {...filePickerProps}
                    {...field}
                    isInvalid={!!error}
                    errorMessage={error?.message}
                    errorMessages={{
                      unsupportedType: __("filePicker.error.unsupportedType"),
                      maxSize: __("filePicker.error.maxSize"),
                      maxFilesReached: __("filePicker.error.maxFilesReached"),
                    }}
                    isRequired
                    maxSize={(filePickerProps.maxSize ?? 0) * 1024} // converts KB to Bytes
                    label={__("loyalty.contact_form.label")}
                    buttonLabel={__("loyalty.contact_form.button_label")}
                    helperText={`${__("loyalty.contact_form.supported_formats")}: ${formatFileTypes(
                      filePickerProps.acceptedFileTypes ?? []
                    )}. ${__("loyalty.contact_form.max_size")}: ${formatFileSize(filePickerProps.maxSize ?? 0)}.`}
                  />
                )}
              />
            )}
            <ConsentCheckbox consent={consent} displayConsent="always" showRequiredAsterisk={showRequiredAsterisk} />
            {contestId && <input type="hidden" {...form.register("contest_id")} value={contestId} />}
            <Button
              type={"submit"}
              disabled={!form.formState.isValid}
              pending={isLoading || isConsentsLoading}
              className={styles.button}
              data-testid="submit-button"
              size={mainBreakpoint === "xs" ? "sm" : "md"}
            >
              {sendButtonLabel ?? __("contact.button.send")}
            </Button>
          </Form>
          {reCaptchaEnabled && brandingInformationEnabled && <Recaptchabar />}
        </>
      )}
    </div>
  );
};
